import { BASEMAP_COARSEST_LEVEL, IDENTITY_ORIENTATION, placesAt } from "@pax/basemap";
import { createTileDecoder, type TileDecoder } from "./decoder";
import { globeLookingAt } from "./map-view";
import { flatFit } from "./projection";
import { createRenderer, type Renderer } from "./renderer";
import { readPublished, readPublishedTile } from "./session";

/**
 * Pictures of published basemaps: each drawn once as a globe from its coarsest tiles, by one renderer
 * the whole page shares, and kept for as long as the page is open. A card's globe sits in a tile's
 * shape for the asset grid; a round one is the globe alone, edge to edge, on nothing.
 */

export type ThumbnailShape = "card" | "round";

const SIZES = {
  card: { size: { width: 480, height: 360 }, share: 0.86 },
  round: { size: { width: 128, height: 128 }, share: 1 },
} as const;

let shared: { renderer: Renderer; canvas: HTMLCanvasElement; decoder: TileDecoder } | null = null;
const pictures = new Map<string, Promise<string>>();
let queue: Promise<unknown> = Promise.resolve();

export function thumbnailOf(
  assetId: string,
  dark: boolean,
  shape: ThumbnailShape = "card",
): Promise<string> {
  const key = `${assetId}:${dark ? "dark" : "light"}:${shape}`;
  const made = pictures.get(key);
  if (made) return made;
  const drawing = queue.then(() => draw(assetId, dark, shape));
  queue = drawing.catch(() => undefined);
  pictures.set(key, drawing);
  return drawing;
}

async function draw(assetId: string, dark: boolean, shape: ThumbnailShape): Promise<string> {
  const { size: SIZE, share } = SIZES[shape];
  if (!shared) {
    const canvas = document.createElement("canvas");
    shared = {
      canvas,
      renderer: createRenderer(canvas, { pages: 6 }),
      decoder: createTileDecoder(),
    };
  }
  const { renderer, canvas, decoder } = shared;
  const published = await readPublished(assetId);
  const { manifest } = published;
  const geometry = manifest.geometry.kind;
  const rasters = Object.entries(manifest.rasters).map(([name, rules]) => ({
    name,
    type: rules.type,
    deepest: BASEMAP_COARSEST_LEVEL,
    step: "step" in rules ? rules.step : 1,
    enlarge: "enlarge" in rules ? rules.enlarge : "nearest",
  }));
  renderer.setWorld({
    orientation: geometry === "sphere" ? manifest.geometry.orientation : IDENTITY_ORIENTATION,
    rasters,
    expressions: manifest.expressions,
  });
  await Promise.all(
    rasters.map(async ({ name, type }) => {
      await Promise.all(
        placesAt(BASEMAP_COARSEST_LEVEL, geometry === "sphere" ? 6 : 1).map(async place => {
          const pixels = await readPublishedTile(published, name, type, place, geometry, decoder);
          if (pixels) renderer.setTile(name, place, pixels);
        }),
      );
    }),
  );
  renderer.draw({
    camera:
      geometry === "sphere"
        ? globeLookingAt(15, 18, SIZE, share)
        : { kind: "flat", x: 0, y: 0, scale: flatFit(SIZE) },
    size: SIZE,
    paint: manifest,
    dark,
    values: {},
    grid: false,
    hidden: new Set(),
    brush: null,
  });
  return canvas.toDataURL("image/webp", 0.86);
}
